<template>
  <div class="user-form-page">
    <Head :title="`${form.first_name} ${form.last_name}`" />

    <div class="mb-6">
      <nav class="mb-2 flex items-center gap-2 text-sm text-gray-500">
        <Link href="/users" class="font-medium text-brand-600 hover:text-brand-800">Users</Link>
        <span>/</span>
        <span class="text-gray-700">Edit</span>
      </nav>
      <div class="flex items-center gap-3">
        <img
          v-if="user.photo"
          class="h-12 w-12 rounded-2xl object-cover ring-1 ring-gray-200"
          :src="user.photo"
          :alt="`${form.first_name} ${form.last_name}`"
        />
        <span
          v-else
          class="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-600 text-sm font-bold text-white"
        >
          {{ initials }}
        </span>
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.14em] text-brand-500">Administration</p>
          <h1 class="mt-0.5 text-3xl font-bold tracking-tight text-brand-900">
            {{ form.first_name }} {{ form.last_name }}
          </h1>
          <p class="mt-1 text-sm text-gray-500">Update account details, role, and agency scope.</p>
        </div>
      </div>
    </div>

    <trashed-message v-if="user.deleted_at" class="mb-5" @restore="restore">
      This user has been deleted.
    </trashed-message>

    <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <form @submit.prevent="update">
        <div class="grid grid-cols-1 gap-5 p-6 md:grid-cols-2 md:p-8">
          <text-input v-model="form.first_name" :error="form.errors.first_name" class="w-full" label="First name" />
          <text-input v-model="form.last_name" :error="form.errors.last_name" class="w-full" label="Last name" />
          <text-input v-model="form.email" :error="form.errors.email" class="w-full" label="Email" />
          <text-input
            v-model="form.password"
            :error="form.errors.password"
            class="w-full"
            type="password"
            autocomplete="new-password"
            label="Password"
          />
          <select-input v-model="form.role" :error="form.errors.role" class="w-full" label="Role">
            <option v-for="(label, value) in roles" :key="value" :value="value">{{ label }}</option>
          </select-input>
          <select-input v-model="form.owner" :error="form.errors.owner" class="w-full" label="Owner">
            <option :value="1">Yes</option>
            <option :value="0">No</option>
          </select-input>
          <file-input
            v-model="form.photo"
            :errors="form.errors.photo ? [form.errors.photo] : []"
            class="w-full md:col-span-2"
            type="file"
            accept="image/*"
            label="Photo"
          />
          <agency-scope-fields
            v-if="form.role === 'agency'"
            v-model:codes="form.agent_codes"
            v-model:columns="form.visible_columns"
            class="md:col-span-2"
            :channel-options="channels"
            :column-options="customerColumns"
            :defaults="defaultColumns"
            :channel-error="scopeError('agent_codes')"
            :column-error="scopeError('visible_columns')"
          />
        </div>

        <div
          v-if="Object.keys(form.errors).length"
          class="mx-6 mb-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 md:mx-8"
        >
          <p class="font-semibold">Please fix the following:</p>
          <ul class="mt-2 list-disc space-y-1 pl-5">
            <li v-for="(message, key) in uniqueErrors" :key="key">{{ message }}</li>
          </ul>
        </div>

        <div class="flex items-center gap-3 border-t border-gray-100 bg-gray-50 px-6 py-4 md:px-8">
          <button
            v-if="!user.deleted_at"
            class="rounded-xl px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
            tabindex="-1"
            type="button"
            @click="destroy"
          >
            Delete User
          </button>
          <div class="ml-auto flex items-center gap-3">
            <Link
              href="/users"
              class="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </Link>
            <loading-button
              :loading="form.processing"
              class="inline-flex items-center justify-center rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-700"
              type="submit"
            >
              Update User
            </loading-button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import FileInput from '@/Shared/FileInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import TrashedMessage from '@/Shared/TrashedMessage'
import AgencyScopeFields from '@/Shared/AgencyScopeFields'

export default {
  components: {
    AgencyScopeFields,
    FileInput,
    Head,
    Link,
    LoadingButton,
    SelectInput,
    TextInput,
    TrashedMessage,
  },
  layout: Layout,
  props: {
    user: Object,
    roles: Object,
    channels: Array,
    customerColumns: Array,
    defaultColumns: Array,
  },
  remember: 'form',
  data() {
    return {
      form: this.$inertia.form({
        _method: 'put',
        first_name: this.user.first_name,
        last_name: this.user.last_name,
        email: this.user.email,
        password: '',
        owner: this.user.owner ? 1 : 0,
        role: this.user.role,
        agent_codes: [...(this.user.agent_codes || [])].map(String),
        visible_columns: [...(this.user.visible_columns || this.defaultColumns || [])].map(String),
        photo: null,
      }),
    }
  },
  computed: {
    initials() {
      const first = this.form.first_name?.[0] || ''
      const last = this.form.last_name?.[0] || ''
      return `${first}${last}`.toUpperCase() || 'U'
    },
    uniqueErrors() {
      const seen = new Set()
      const messages = []
      Object.values(this.form.errors || {}).forEach((message) => {
        if (!message || seen.has(message)) return
        seen.add(message)
        messages.push(message)
      })
      return messages.slice(0, 8)
    },
  },
  methods: {
    scopeError(prefix) {
      if (this.form.errors[prefix]) return this.form.errors[prefix]
      const match = Object.keys(this.form.errors || {}).find(
        (key) => key === prefix || key.startsWith(`${prefix}.`),
      )
      return match ? this.form.errors[match] : null
    },
    update() {
      this.form
        .transform((data) => ({
          ...data,
          owner: data.owner === true || data.owner === 1 || data.owner === '1',
          agent_codes: [...(data.agent_codes || [])].map(String),
          visible_columns: [...(data.visible_columns || [])].map(String),
        }))
        .post(`/users/${this.user.id}`, {
          onSuccess: () => this.form.reset('password', 'photo'),
        })
    },
    destroy() {
      if (confirm('Are you sure you want to delete this user?')) {
        this.$inertia.delete(`/users/${this.user.id}`)
      }
    },
    restore() {
      if (confirm('Are you sure you want to restore this user?')) {
        this.$inertia.put(`/users/${this.user.id}/restore`)
      }
    },
  },
}
</script>
